/**
 * AppTour — Tour contextual que señala cada sección del menú con spotlight + tooltip.
 * Se muestra DENTRO de la app real, sobre el layout.
 */
import { useState, useEffect, useRef } from "react";
import { P, FF } from "../../styles/tokens";

const STEPS = [
  {
    target: "[data-tour='hero']",
    text: "Aquí ves cuánto dinero tienes disponible en tiempo real. Toca para explorar.",
    position: "below",
  },
  {
    target: "[data-tour='tab-inicio']",
    text: "Tu dashboard: a dónde va tu dinero, alertas y movimientos del mes.",
    position: "above",
  },
  {
    target: "[data-tour='tab-analisis']",
    text: "Descubre en qué gastas más, cuánto puedes gastar por día y si vas mejor que el mes pasado.",
    position: "above",
  },
  {
    target: "[data-tour='tab-anual']",
    text: "El panorama completo: compara todos tus meses de un vistazo.",
    position: "above",
  },
  {
    target: "[data-tour='tab-config']",
    text: "Configura tus cuentas, presupuestos y gastos recurrentes.",
    position: "above",
  },
  {
    target: "[data-tour='duky-fab']",
    text: "¡Soy Duky! 🐿️ Toca aquí para registrar gastos con voz, escribirlos o pedirme un consejo.",
    position: "above",
    isLast: true,
  },
];

export default function AppTour({ onComplete }) {
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState(null);
  const [visible, setVisible] = useState(false);

  // Delay inicial para que la app termine de renderizar
  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 600);
    return () => clearTimeout(timer);
  }, []);

  // Encontrar el elemento target y su posición
  useEffect(() => {
    if (!visible) return;
    const s = STEPS[step];
    const el = document.querySelector(s.target);
    if (el) {
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    } else {
      // Si no encuentra el elemento, saltar al siguiente
      if (step < STEPS.length - 1) setStep(step + 1);
      else finish();
    }
  }, [step, visible]);

  // Recalcular al resize/scroll
  useEffect(() => {
    if (!visible) return;
    const recalc = () => {
      const s = STEPS[step];
      const el = document.querySelector(s.target);
      if (el) {
        const r = el.getBoundingClientRect();
        setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      }
    };
    window.addEventListener("resize", recalc);
    window.addEventListener("scroll", recalc, true);
    return () => {
      window.removeEventListener("resize", recalc);
      window.removeEventListener("scroll", recalc, true);
    };
  }, [step, visible]);

  const next = () => {
    if (step < STEPS.length - 1) {
      setRect(null);
      setTimeout(() => setStep(step + 1), 50);
    } else {
      finish();
    }
  };

  const finish = () => {
    setVisible(false);
    try { localStorage.setItem("arbo_tour_done", "true"); } catch {}
    onComplete?.();
  };

  if (!visible || !rect) return null;

  const s = STEPS[step];
  const pad = 8;
  const holeTop = rect.top - pad;
  const holeLeft = rect.left - pad;
  const holeW = rect.width + pad * 2;
  const holeH = rect.height + pad * 2;
  const holeR = 16;

  // Tooltip position
  const tooltipBelow = s.position === "below";
  const tooltipStyle = {
    position: "fixed",
    zIndex: 9001,
    left: 24,
    right: 24,
    maxWidth: 320,
    ...(tooltipBelow
      ? { top: holeTop + holeH + 16 }
      : { bottom: window.innerHeight - holeTop + 16 }),
    background: "#fff",
    borderRadius: 14,
    padding: "16px 18px",
    boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
    opacity: rect ? 1 : 0,
    transition: "opacity .2s, top .3s, bottom .3s",
  };

  // SVG overlay con hueco
  const svgW = window.innerWidth;
  const svgH = window.innerHeight;

  return (
    <>
      {/* Overlay con hueco */}
      <svg
        onClick={next}
        style={{ position: "fixed", inset: 0, zIndex: 9000, cursor: "pointer" }}
        width={svgW} height={svgH}
      >
        <defs>
          <mask id="tour-mask">
            <rect width={svgW} height={svgH} fill="white" />
            <rect x={holeLeft} y={holeTop} width={holeW} height={holeH} rx={holeR} fill="black" />
          </mask>
        </defs>
        <rect width={svgW} height={svgH} fill="rgba(0,0,0,0.6)" mask="url(#tour-mask)" />
      </svg>

      {/* Tooltip */}
      <div style={tooltipStyle}>
        <div style={{ fontSize: 15, color: P.text, fontFamily: FF, lineHeight: 1.5, marginBottom: 14 }}>
          {s.text}
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          {/* Dots */}
          <div style={{ display: "flex", gap: 6 }}>
            {STEPS.map((_, i) => (
              <div key={i} style={{
                width: i === step ? 8 : 6, height: i === step ? 8 : 6,
                borderRadius: "50%",
                background: i === step ? P.ingresos.base : i < step ? P.ingresos.tint : "#D1D5DB",
                transition: "all .2s",
              }} />
            ))}
          </div>
          {/* Botón */}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); next(); }}
            style={{
              padding: s.isLast ? "8px 20px" : "6px 14px",
              borderRadius: 10,
              border: "none",
              background: s.isLast ? P.ingresos.base : "transparent",
              color: s.isLast ? "#fff" : P.ingresos.base,
              fontFamily: FF, fontSize: 14, fontWeight: 700,
              cursor: "pointer",
            }}
          >
            {s.isLast ? "Entendido" : "Siguiente →"}
          </button>
        </div>
      </div>
    </>
  );
}
